import { Indent } from "@stenajs-webui/core";
import {
  BreadCrumbs,
  ResultListBanner,
  ResultListBannerState,
  SecondaryButton,
  stenaSailingRoute,
} from "@stenajs-webui/elements";
import { ActionPrompt, Popover } from "@stenajs-webui/tooltip";
import _, { uniq } from "lodash";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingModalWithLock } from "@/components/loading-modals/LoadingModalWithLock";
import { PageHeader } from "@/components/page-header/PageHeader";
import { StoreState } from "@/redux/RootReducer";

import { usePrompt } from "@/hooks/usePrompt";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { RouteAgreementsRatesByFilterQuery } from "@/gql/graphql";
import { useParams } from "react-router";
import { routeFactory } from "../../../../../RouteFactory";
import { Crumb } from "@/components/bread-crumbs/Crumb";
import { bulkEditRatesActions } from "../redux/reducer";
import { bulkEditRatesSelectors } from "../redux/selectors";
import { BulkEditRatesSaveButton } from "./BulkEditRatesSaveButton";

export interface Props {
  custIndex: string;
  routeAgreementRates: RouteAgreementsRatesByFilterQuery | undefined;
  headAgreementNumber: string;
  refetch: () => Promise<unknown>;
}

export const BulkEditRatesHeader: React.FC<Props> = ({
  custIndex,
  routeAgreementRates,
  headAgreementNumber,
  refetch,
}) => {
  const dispatch = useDispatch();
  const { headAgreementId, customerId } = useParams() as {
    headAgreementId: string;
    customerId: string;
  };

  const saveChangesLoading = useSelector(
    bulkEditRatesSelectors.getSaveChangesLoading
  );
  const originalValueRow = useSelector(
    bulkEditRatesSelectors.getPersistedBulkEditRatesTableRows
  );
  const editableValueRow = useSelector(
    bulkEditRatesSelectors.getEditedBulkEditRatesTableRows
  );

  const hasNoChanges =
    _.isEqual(originalValueRow, editableValueRow) || saveChangesLoading;

  usePrompt({
    message: "You have unsaved changes, would you like to leave the page?",
    shouldBlock: !hasNoChanges,
  });

  const errorSelector = (state: StoreState) =>
    state.bulkEditRatesState.saveErrors;
  const saveErrors = useSelector(errorSelector);
  const bannerState = useMemo<ResultListBannerState | undefined>(() => {
    if (saveErrors && saveErrors.length) {
      const distinctErrorMessages = uniq(
        saveErrors.map((error) => error.message)
      );

      return {
        headerText: "Unable to save",
        items: uniq(
          distinctErrorMessages.map((e) => ({
            text: e,
          }))
        ),
      };
    }
  }, [saveErrors]);

  return (
    <>
      <PageHeader
        icon={stenaSailingRoute}
        title={"Edit rates"}
        width={"100vw"}
        sticky
        offsetLeft={0}
        offsetTop={0}
        zIndex={ZIndex.low}
        contentUnder={
          bannerState ? (
            <ResultListBanner bannerState={bannerState} variant={"error"} />
          ) : undefined
        }
        contentRight={
          <>
            <Popover
              placement={"bottom"}
              trigger={"click"}
              renderTrigger={(props) => (
                <SecondaryButton
                  label={"Discard"}
                  disabled={hasNoChanges}
                  {...props}
                />
              )}
            >
              {({ onRequestClose }) => (
                <ActionPrompt
                  onNo={onRequestClose}
                  onYes={async () => {
                    onRequestClose();
                    dispatch(bulkEditRatesActions.clearSaveErrors());
                    dispatch(bulkEditRatesActions.revertAll());
                    refetch();
                  }}
                />
              )}
            </Popover>
            <Indent />
            <BulkEditRatesSaveButton
              disabled={hasNoChanges}
              routeAgreementRates={routeAgreementRates}
            />
          </>
        }
        breadcrumbs={
          <BreadCrumbs>
            <Crumb
              label={custIndex}
              path={routeFactory.productAndPrice.customer.customer({
                customerId,
              })}
              dataTestid={testIdConstants.breadcrumbCustomer}
            />
            <Crumb
              label={headAgreementNumber}
              path={routeFactory.productAndPrice.customer.customerHeadAgreement(
                {
                  customerId,
                  headAgreementId,
                }
              )}
              dataTestid={testIdConstants.breadcrumbHeaderAgreement}
            />
            <Crumb label={"Edit rates"} />
          </BreadCrumbs>
        }
      />
      {saveChangesLoading && (
        <LoadingModalWithLock label="Saving new rates to all route agreements. Thanks for your patience" />
      )}
    </>
  );
};
